import { Form, Formik, useFormikContext } from 'formik'
import { ChangeEvent } from 'react'
import { useTranslation } from 'react-i18next'
import * as Yup from 'yup'
import { getItemAmountForPeriod } from '../../../lib/Calculator'
import { getDateFromYearAndMonth } from '../../../lib/Dates'
import {
  SheetCategory,
  Spend,
  SpendAmount,
  SpendAmountCreate,
  SpendUpdate,
} from '../../../lib/Types'
import Headline from '../../Elements/Headline'
import Button from '../../Formik/Button'
import Input from '../../Formik/Input'
import Select from '../../Formik/Select'
import { SpendFrequency } from '../lib/Enums/SpendFrequency'
import { SpendType } from '../lib/Enums/SpendType'
import { useBudget } from '../lib/Hooks/Budget'
import { useCategories } from '../lib/Hooks/Categories'
import { useExpenses } from '../lib/Hooks/Spends'

interface Props {
  spend: Spend
  saveSpend: (spend: SpendUpdate) => void
}

const spendTypeWordKey = (type: SpendType): string => {
  switch (type) {
    case SpendType.EXPENSE:
      return 'budget:shared.expenseWord'
    case SpendType.SAVING:
      return 'budget:shared.savingWord'
    default:
      return 'budget:shared.incomeWord'
  }
}

const SpendConfiguration = ({ spend, saveSpend }: Props) => {
  const { t } = useTranslation(['budget', 'common'])
  const { data: budget } = useBudget()
  const { data: categories } = useCategories(budget!)
  const { data: expenses } = useExpenses(budget!)

  return (
    <>
      <Headline className='mb-5'>
        {t('budget:spendConfiguration.editTitle', {
          type: t(spendTypeWordKey(spend.type)),
        })}
      </Headline>
      <Formik
        initialValues={{
          title: spend.title,
          category: spend.category ? spend.category.id : '',
          description: spend.description || '',
          amount: parseFloat(spend.amounts.at(-1)!.amount.toFixed(2)),
          payment_month: new Date(spend.accounting_date).getMonth() + 1,
          frequency: spend.amounts.at(-1)?.frequency,
          parent_id: spend.parent_id || '',
        }}
        validationSchema={Yup.object({
          title: Yup.string().required(t('common:validation.required')),
          category: Yup.string(),
          description: Yup.string().optional(),
          payment_month: Yup.string().required(
            t('common:validation.required'),
          ),
          parent_id: Yup.string(),
        })}
        onSubmit={(values) => {
          const amounts = structuredClone(spend.amounts) as SpendAmountCreate[]
          const latestAmount = amounts.at(-1)! as SpendAmount

          if (
            latestAmount.amount !== values.amount ||
            latestAmount.frequency !== values.frequency
          ) {
            amounts.push({
              formula: values.amount.toString(),
              frequency: values.frequency,
            })
          }

          const parentId = values.parent_id
            ? parseInt(values.parent_id as string, 10)
            : null

          const newSpend = {
            ...spend,
            amounts,
            title: values.title,
            description: values.description,
            accounting_date: getDateFromYearAndMonth(
              new Date().getFullYear(),
              values.payment_month,
            ),
            parent_id: parentId,
          }

          if (spend.type === SpendType.EXPENSE) {
            const parentSpend = parentId
              ? expenses!.find((expense) => expense.id === parentId)
              : undefined

            newSpend.category = parentSpend
              ? parentSpend.category
              : (categories!.find(
                (category) =>
                  category.id === parseInt(values.category as string, 10),
              ) as SheetCategory)
          }

          saveSpend(newSpend)
        }}
      >
        <SpendConfigurationForm spend={spend} expenses={expenses || []} />
      </Formik>
    </>
  )
}

const SpendConfigurationForm = ({
  spend,
  expenses,
}: {
  spend: Spend
  expenses: Spend[]
}) => {
  const { t } = useTranslation(['budget'])
  const { data: budget } = useBudget()
  const { data: categories } = useCategories(budget!)
  const { values, setFieldValue } = useFormikContext<any>()
  const { frequency } = values

  const handleFrequencyChange = (event: ChangeEvent<HTMLSelectElement>) => {
    setFieldValue('frequency', event.target.value)
    setFieldValue(
      'amount',
      getItemAmountForPeriod(spend, event.target.value as SpendFrequency),
    )
  }
  const hasParentSelected = () => {
    return !!values.parent_id
  }
  const hasChildren = () => {
    return !!spend.children && spend.children.length > 0
  }
  const availableParents = expenses.filter(
    (expense) => !expense.parent_id && expense.id !== spend.id,
  )

  return (
    <Form>
      <p className='mb-2'>
        {t('budget:spendConfiguration.nameLabel', {
          type: t(spendTypeWordKey(spend.type)),
        })}
      </p>
      <Input
        name='title'
        placeholder={t('budget:spendConfiguration.namePlaceholder')}
        size='sm'
        className='w-full mb-3'
      />
      <p className='mb-2'>{t('budget:shared.note')}</p>
      <Input
        name='description'
        placeholder={t('budget:spendConfiguration.notePlaceholder')}
        size='sm'
        className='w-full mb-3'
      />
      {spend.type === SpendType.EXPENSE && !hasParentSelected() && (
        <>
          <p className='mb-2'>{t('budget:shared.categoryLabel')}</p>
          <Select name='category' className='mb-4'>
            {categories!.map((category) => (
              <option key={category.id} value={category.id}>
                {category.name}
              </option>
            ))}
          </Select>
        </>
      )}
      {spend.type === SpendType.EXPENSE && (
        <>
          <p className='mb-2'>{t('budget:spendConfiguration.parentLabel')}</p>
          <Select
            name='parent_id'
            className={hasChildren() ? 'mb-1' : 'mb-4'}
            disabled={hasChildren()}
          >
            <option value=''>
              {t('budget:spendConfiguration.noParentOption')}
            </option>
            {availableParents.map((parent) => (
              <option key={parent.id} value={parent.id}>
                {parent.title}
              </option>
            ))}
          </Select>
          {hasChildren() && (
            <p className='mb-3 text-sm italic'>
              {t('budget:spendConfiguration.parentDisabledHint')}
            </p>
          )}
        </>
      )}
      {spend.type === SpendType.EXPENSE && (
        <>
          <p className='mb-2'>{t('budget:spendConfiguration.amountLabel')}</p>
          <Input
            name='amount'
            type='number'
            placeholder={t('budget:spendConfiguration.amountLabel')}
            size='sm'
            className='w-full mb-3'
          />
        </>
      )}
      <p className='mb-2'>
        {spend.type === SpendType.EXPENSE
          ? t('budget:spendConfiguration.paymentFrequencyLabel')
          : t('budget:spendConfiguration.incomeFrequencyLabel')}
      </p>
      <Select
        name='frequency'
        className='mb-3'
        onChange={handleFrequencyChange}
      >
        <option value={SpendFrequency.MONTHLY}>
          {t('budget:shared.frequency.monthly')}
        </option>
        <option value={SpendFrequency.BIMONTHLY}>
          {t('budget:shared.frequency.bimonthly')}
        </option>
        <option value={SpendFrequency.QUARTERLY}>
          {t('budget:shared.frequency.quarterly')}
        </option>
        <option value={SpendFrequency.BIYEARLY}>
          {t('budget:shared.frequency.biyearly')}
        </option>
        <option value={SpendFrequency.YEARLY}>
          {t('budget:shared.frequency.yearly')}
        </option>
      </Select>

      {spend.type === SpendType.EXPENSE &&
        frequency === SpendFrequency.YEARLY && (
        <>
          <p className='mb-2'>{t('budget:shared.paymentMonthLabel')}</p>
          <Select name='payment_month' className='mb-4'>
            <option value='1'>{t('budget:shared.months.1')}</option>
            <option value='2'>{t('budget:shared.months.2')}</option>
            <option value='3'>{t('budget:shared.months.3')}</option>
            <option value='4'>{t('budget:shared.months.4')}</option>
            <option value='5'>{t('budget:shared.months.5')}</option>
            <option value='6'>{t('budget:shared.months.6')}</option>
            <option value='7'>{t('budget:shared.months.7')}</option>
            <option value='8'>{t('budget:shared.months.8')}</option>
            <option value='9'>{t('budget:shared.months.9')}</option>
            <option value='10'>{t('budget:shared.months.10')}</option>
            <option value='11'>{t('budget:shared.months.11')}</option>
            <option value='12'>{t('budget:shared.months.12')}</option>
          </Select>
        </>
      )}
      {spend.type === SpendType.EXPENSE &&
        frequency === SpendFrequency.BIYEARLY && (
        <>
          <p className='mb-2'>{t('budget:shared.paymentMonthsLabel')}</p>
          <Select name='payment_month' className='mb-4'>
            <option value='1'>{t('budget:shared.biyearlyMonths.1')}</option>
            <option value='2'>{t('budget:shared.biyearlyMonths.2')}</option>
            <option value='3'>{t('budget:shared.biyearlyMonths.3')}</option>
            <option value='4'>{t('budget:shared.biyearlyMonths.4')}</option>
            <option value='5'>{t('budget:shared.biyearlyMonths.5')}</option>
            <option value='6'>{t('budget:shared.biyearlyMonths.6')}</option>
          </Select>
        </>
      )}
      {spend.type === SpendType.EXPENSE &&
        frequency === SpendFrequency.QUARTERLY && (
        <>
          <p className='mb-2'>{t('budget:shared.paymentMonthsLabel')}</p>
          <Select name='payment_month' className='mb-4'>
            <option value='1'>{t('budget:shared.quarterlyMonths.1')}</option>
            <option value='2'>{t('budget:shared.quarterlyMonths.2')}</option>
            <option value='3'>{t('budget:shared.quarterlyMonths.3')}</option>
          </Select>
        </>
      )}
      {spend.type === SpendType.EXPENSE &&
        frequency === SpendFrequency.BIMONTHLY && (
        <>
          <p className='mb-2'>{t('budget:shared.paymentMonthsLabel')}</p>
          <Select name='payment_month' className='mb-4'>
            <option value='1'>{t('budget:shared.bimonthlyMonths.1')}</option>
            <option value='2'>{t('budget:shared.bimonthlyMonths.2')}</option>
          </Select>
        </>
      )}
      <div className='flex justify-end'>
        <Button type='submit' className='mt-3'>
          {t('budget:shared.save')}
        </Button>
      </div>
    </Form>
  )
}

export default SpendConfiguration
